import { useEffect, useRef } from "react";
import { Mail, MapPin, Clock } from "lucide-react";
import { useLang } from "../LanguageContext";

export default function ContactoSection() {
  const { t } = useLang();
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("fade-in-visible")),
      { threshold: 0.1 }
    );
    const els = ref.current?.querySelectorAll(".fade-in-item");
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="contacto"
      ref={ref}
      data-testid="contacto-section"
      className="bg-[#1A1A18] py-24 md:py-36"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-2xl">
          {/* Caption */}
          <p
            className="fade-in-item text-xs uppercase tracking-[0.4em] text-[#C8A96E] mb-4"
            style={{ fontFamily: "Lato, sans-serif" }}
          >
            {t.contacto.caption}
          </p>
          <div className="fade-in-item w-12 h-px bg-[#C8A96E] mb-10" />

          {/* Heading */}
          <h2
            className="fade-in-item text-4xl md:text-5xl font-light text-[#F5F0E8] leading-tight mb-6"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            {t.contacto.heading}
          </h2>
          <p
            className="fade-in-item text-base text-[#F5F0E8]/60 leading-relaxed mb-14"
            style={{ fontFamily: "Lato, sans-serif", fontWeight: 300 }}
          >
            {t.contacto.subheading}
          </p>

          {/* Contact items */}
          <div className="fade-in-item space-y-6 mb-12">
            <div className="flex items-center gap-5">
              <div className="w-11 h-11 flex items-center justify-center border border-[#C8A96E]/30 flex-shrink-0">
                <Mail size={17} className="text-[#C8A96E]" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#C8A96E] mb-1" style={{ fontFamily: "Lato, sans-serif" }}>Email</p>
                <a
                  data-testid="contact-email-link"
                  href="mailto:info@torreamat.com"
                  className="text-base text-[#F5F0E8] hover:text-[#C8A96E] transition-colors"
                  style={{ fontFamily: "Lato, sans-serif" }}
                >
                  info@torreamat.com
                </a>
              </div>
            </div>

            <div className="flex items-center gap-5">
              <div className="w-11 h-11 flex items-center justify-center border border-[#C8A96E]/30 flex-shrink-0">
                <MapPin size={17} className="text-[#C8A96E]" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#C8A96E] mb-1" style={{ fontFamily: "Lato, sans-serif" }}>Ubicación</p>
                <p className="text-base text-[#F5F0E8]" style={{ fontFamily: "Lato, sans-serif" }}>
                  Carrer de la Duquessa d'Orleans, 9
                  <br />
                  Sarrià-Sant Gervasi, 08034 Barcelona
                </p>
              </div>
            </div>

            <div className="flex items-center gap-5">
              <div className="w-11 h-11 flex items-center justify-center border border-[#C8A96E]/30 flex-shrink-0">
                <Clock size={17} className="text-[#C8A96E]" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#C8A96E] mb-1" style={{ fontFamily: "Lato, sans-serif" }}>Respuesta</p>
                <p className="text-base text-[#F5F0E8]" style={{ fontFamily: "Lato, sans-serif" }}>
                  En menos de 24 horas
                </p>
              </div>
            </div>
          </div>

          {/* CTA — opens email client */}
          <div className="fade-in-item">
            <a
              data-testid="contact-email-cta"
              href="mailto:info@torreamat.com?subject=Solicitud%20de%20informaci%C3%B3n%20para%20evento"
              className="inline-flex items-center gap-3 px-10 py-4 bg-[#2D4A2D] text-[#F5F0E8] text-xs uppercase tracking-[0.3em] hover:bg-[#C8A96E] hover:text-[#1A1A18] transition-all duration-300"
              style={{ fontFamily: "Lato, sans-serif" }}
            >
              <Mail size={15} strokeWidth={1.5} />
              {t.contacto.enviar}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
